// libraries imports
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// Core Components
import Loader from "../../../../core/common/loader";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import CommonSelect from "../../../../core/common/commonSelect";
import { notify } from "../../../../services/toast.config";

// Other components
import AddReportTypeModal from "./components/AddReportTypeModal";
import ReportTypesList from "./components/ReportTypeLists";
import ReportTypeFilter from "./components/ReportTypeFilter";

// Helpers
import api from "../../../../services/api";
import { truncateCompanyText } from "../../../../helper/company_helper";
import {
  handleCompanySelection,
  getCompanyOptions,
} from "./helpers/company.handler";
import UpdateReportTypeModal from "./components/UpdateReportTypeModal";
import { FormData } from "./types";

const ReportType = () => {
  // Initialization
  const signature: string | null = localStorage.getItem("signature");

  // STATE MANAGEMENT
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [designations, setDesignations] = useState<any[]>([]);
  const [companyOptions, setCompanyOptions] = useState<any[]>([]);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState({
    company_name: "Select Company",
    company_id: "",
    company_type: "Type",
  });
  const [reportTypes, setReportTypes] = useState<any[]>([]);
  const [filteredReportTypes, setFilteredReportTypes] = useState<any[]>([]);
  const [editData, setEditData] = useState<FormData>();

  // API Calls

  const fetchAllData = async (): Promise<void> => {
    try {
      const [
        getCompaniesResponse,
        getDepartmentResponse,
        getDesignationResponse,
      ] = await Promise.allSettled([
        api.post("userService/getAllCompanyList", {
          signature: signature,
        }),
        api.post("userService/getDepartment", {
          signature: signature,
        }),
        api.post("userService/getDesignationForERS", {
          signature: signature,
        }),
      ]);

      const companies =
        getCompaniesResponse.status === "fulfilled"
          ? getCompaniesResponse.value.data?.data?.data || []
          : [];

      const departments =
        getDepartmentResponse.status === "fulfilled"
          ? getDepartmentResponse.value.data?.data?.data || []
          : [];

      const designations =
        getDesignationResponse.status === "fulfilled"
          ? getDesignationResponse.value.data?.data?.data || []
          : [];

      setCompanies(companies);
      setDepartments(departments);
      setDesignations(designations);
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load data: Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  const updateUI = async () => {
    setIsLoading(true);
    const startTime = Date.now();
    const minDuration = 300;
    try {
      const companyId = selectedCompanyOption?.company_id;
      const companyType = selectedCompanyOption?.company_type;
      if (!companyId || !companyType) {
        throw new Error("Company not selected!");
      }

      const [getEmployeeResponse, getReportTypesResponse] =
        await Promise.allSettled([
          api.post("userService/getAllEmployeesListDataByCompanyId", {
            signature: signature,
            companyId: parseInt(companyId),
            companyType: companyType,
          }),
          api.post("ers/report-types/getAllReportTypes", {
            signature: signature,
            company_id: parseInt(companyId),
            company_type: companyType,
          }),
        ]);

      const employees =
        getEmployeeResponse.status === "fulfilled"
          ? getEmployeeResponse.value.data?.data?.data?.allUsers || []
          : [];

      const reportTypes =
        getReportTypesResponse.status === "fulfilled"
          ? getReportTypesResponse.value.data?.data || []
          : [];

      setEmployees(employees);
      setReportTypes(reportTypes);
      setFilteredReportTypes(reportTypes);
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load data: Internal Server Error!";
      }
      notify.error(errorMessage);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = minDuration - elapsed;

      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleReportTypeDelete = async (
    report_type_id: number | any
  ): Promise<void> => {
    try {
      const companyId = selectedCompanyOption?.company_id;
      const companyType = selectedCompanyOption?.company_type;
      if (!companyId || !companyType || !report_type_id) {
        throw new Error("Company not selected!");
      }

      const dataToSend = {
        signature: signature,
        company_id: parseInt(companyId),
        company_type: companyType,
        report_type_id: report_type_id,
      };

      const getReportTypesResponse = await api.post(
        "/ers/report-types/deleteReportType",
        dataToSend
      );

      notify.success("Report Type Deleted!");
      updateUI();
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  const handleReportTypeDuplicate = async (
    report_type_id: number | any
  ): Promise<void> => {
    try {
      const companyId = selectedCompanyOption?.company_id;
      const companyType = selectedCompanyOption?.company_type;
      if (!companyId || !companyType || !report_type_id) {
        throw new Error("Company not selected!");
      }

      const dataToSend = {
        signature: signature,
        company_id: parseInt(companyId),
        company_type: companyType,
        report_type_id: report_type_id,
      };

      const getReportTypesResponse = await api.post(
        "/ers/report-types/duplicateReportType",
        dataToSend
      );

      notify.success("Report Type Duplicated!");
      updateUI();
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    if (selectedCompanyOption.company_id) {
      updateUI();
    }
  }, [selectedCompanyOption.company_id, selectedCompanyOption.company_type]);

  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedERSCompany");

      let currentData = {
        value: companies[0].id.toString(),
        label: companies[0].name + " - " + companies[0].type,
      };

      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        currentData = {
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        };
      }
      handleCompanySelection(currentData, setSelectedCompanyOption);
      const generateCompanyOptions = getCompanyOptions(companies);
      setCompanyOptions(generateCompanyOptions);
    }
  }, [companies]);

  useEffect(() => {
    fetchAllData();
  }, []);

  // RENDER COMPONENT

  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header Section */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">📃 Report Types</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Reports Settings</li>
                  <li className="breadcrumb-item">Report Type</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <CommonSelect
                className="select mb-2 me-2"
                options={companyOptions}
                defaultValue={{
                  value: selectedCompanyOption.company_id,
                  label:
                    truncateCompanyText(
                      selectedCompanyOption.company_name,
                      20
                    ) +
                    "-" +
                    selectedCompanyOption.company_type,
                }}
                onChange={(selectedOption: any) =>
                  handleCompanySelection(
                    selectedOption,
                    setSelectedCompanyOption
                  )
                }
              />

              <div className="mb-2 me-2">
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#ers_create_report_type_modal"
                  data-inert={true}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Create Report Type
                </Link>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Content Area - This is where child components render */}
          <div className="reports-content">
            <ReportTypeFilter
              data={reportTypes}
              setFilteredData={setFilteredReportTypes}
            />

            <ReportTypesList
              reportTypes={reportTypes}
              filteredReportTypes={filteredReportTypes}
              employees={employees}
              departments={departments}
              designations={designations}
              handleReportTypeDelete={handleReportTypeDelete}
              setEditData={setEditData}
              handleReportTypeDuplicate={handleReportTypeDuplicate}
            />
          </div>
        </div>

        {/* Footer Section */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <AddReportTypeModal
        employees={employees}
        departments={departments}
        designations={designations}
        updateUI={updateUI}
        selectedCompanyOption={selectedCompanyOption}
      />

      <UpdateReportTypeModal
        employees={employees}
        departments={departments}
        designations={designations}
        updateUI={updateUI}
        selectedCompanyOption={selectedCompanyOption}
        editData={editData}
      />
    </>
  );
};

export default ReportType;
